/**
 * Fit-score thresholds and branding for one customer. Talks directly to
 * avrio-backend's GET/PUT /api/customers/{customerId} - no FSM, matching the
 * plain-axios pattern already used for the sibling per-user permission
 * override drawer/badge in this same admin/configuration tooling priority.
 *
 * Access mirrors the backend exactly: an Admin acts on their own customer
 * only (no picker - there is nothing to pick), a Super Admin can act on any
 * customer and sees a picker first. Neither role is offered here at all
 * unless canSeeSettingsNavigationButton is true (Admin/Super Admin only,
 * same matrix avrio-backend's CustomerService#getCustomer authorizes).
 */

import { memo, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from '../../../../axiosConfig';
import Layout from '../../../../components/layout';
import PageHeading from '../../../../components/common/page-heading';
import { User } from '../../../../fsm/users';
import { UserRole } from '../../../users/utils/role';
import CreateCustomerModal from '../../components/create-customer-modal';
import CustomerPlanSection from '../../components/customer-plan-section';
import YourPlanSection from '../../components/your-plan-section';
import SyntheticCustomerSection from '../../components/synthetic-customer-section';
import CustomerCountrySection from '../../components/customer-country-section';
import CustomerTierSection from '../../components/customer-tier-section';
import SponsorshipPolicySection from '../../components/sponsorship-policy-section';
import { customerLabel } from '../../utils/customerLabel';
import MatchingUsageSection from '../../components/matching-usage-section';
import EstateMatchingSection from '../../components/estate-matching-section';
import { CustomerConfig, CustomerListItem } from '../../types';
import styles from './index.module.css';

const { Text, Title } = Typography;

/**
 * avrio-backend's `invitations.recommended-fit-score-threshold` (application.yml),
 * the value used when neither the customer nor a job sets one. Mirrored here
 * only to tell the person what "not set" means; the backend stays the source
 * of truth, so a change there needs this constant to follow.
 */
const RECOMMENDED_THRESHOLD = 65;

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/** The backend's own reason for a refusal, when it sent one. */
const saveErrorMessage = (err: unknown): string => {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' && message.trim()
    ? `Could not save: ${message}`
    : 'Could not save. Try again.';
};

interface Props {
  user?: User;
}

interface FormValues {
  applicantFitScoreThreshhold?: number;
  candidateFitScoreThreshhold?: number;
  logoUrl?: string;
}

const CustomerSettings = ({ user }: Props) => {
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    number | undefined
  >(isSuperAdmin ? undefined : user?.customerId);
  const [config, setConfig] = useState<CustomerConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Three separate failures, each with its own message and recovery. They used
  // to share one banner with no way back, so a failed load left an empty page
  // until the whole thing was refreshed. `error` is now only a failed save.
  const [error, setError] = useState<string | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [configFailed, setConfigFailed] = useState(false);
  const [configAttempt, setConfigAttempt] = useState(0);
  const [saved, setSaved] = useState(false);
  // True once the form differs from what was last loaded or saved. Drives the
  // Save button, the leave-page warning and the customer-switch confirmation.
  const [dirty, setDirty] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const logoValue = Form.useWatch('logoUrl', form);

  const loadCustomers = useCallback(() => {
    setCustomersLoading(true);
    setListFailed(false);
    axios
      .get<CustomerListItem[]>('/api/customers')
      .then((res) => {
        // A response of the wrong shape is a failed load, not "no customers".
        if (!Array.isArray(res.data)) {
          setListFailed(true);
          return;
        }
        setCustomers(res.data);
      })
      .catch(() => setListFailed(true))
      .finally(() => setCustomersLoading(false));
  }, []);

  useEffect(() => {
    if (isSuperAdmin) loadCustomers();
  }, [isSuperAdmin, loadCustomers]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    let cancelled = false;
    setConfigLoading(true);
    setConfigFailed(false);
    setError(null);
    setSaved(false);
    setDirty(false);
    axios
      .get<CustomerConfig>(`/api/customers/${selectedCustomerId}`)
      .then((res) => {
        if (cancelled) return;
        setConfig(res.data);
        form.setFieldsValue({
          applicantFitScoreThreshhold: res.data.applicantFitScoreThreshhold,
          candidateFitScoreThreshhold: res.data.candidateFitScoreThreshhold,
          logoUrl: res.data.logoUrl,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Never leave the previous customer's form up under a failure for
        // this one.
        setConfig(null);
        setConfigFailed(true);
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, form, configAttempt]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    setLogoBroken(false);
  }, [logoValue]);

  const handlePickCustomer = useCallback(
    (customerId: number) => {
      if (customerId === selectedCustomerId) return;
      if (!dirty) {
        setSelectedCustomerId(customerId);
        return;
      }
      Modal.confirm({
        title: 'Discard unsaved changes?',
        content: 'You have changes to this customer that are not saved.',
        okText: 'Discard and switch',
        cancelText: 'Keep editing',
        onOk: () => setSelectedCustomerId(customerId),
      });
    },
    [dirty, selectedCustomerId],
  );

  // The new flag, as the backend now has it: the section's own state, and the picker's label.
  const handleSyntheticChanged = useCallback((updated: CustomerConfig) => {
    setConfig((previous) =>
      previous
        ? {
            ...previous,
            isSynthetic: updated.isSynthetic,
            syntheticReason: updated.syntheticReason,
          }
        : previous,
    );
    setCustomers((previous) =>
      previous.map((customer) =>
        customer.id === updated.id
          ? { ...customer, isSynthetic: updated.isSynthetic }
          : customer,
      ),
    );
  }, []);

  const handleTierChanged = useCallback((updated: CustomerConfig) => {
    setConfig((previous) =>
      previous ? { ...previous, tier: updated.tier } : previous,
    );
  }, []);

  const handleCountryChanged = useCallback((updated: CustomerConfig) => {
    setConfig((previous) =>
      previous ? { ...previous, countryCode: updated.countryCode } : previous,
    );
  }, []);

  const handleCreated = useCallback((customer: CustomerListItem) => {
    setCustomers((prev) => [...prev, customer]);
    setCreateOpen(false);
    setSelectedCustomerId(customer.id);
  }, []);

  const handleSave = useCallback(
    (values: FormValues) => {
      if (!selectedCustomerId) return;
      setSaving(true);
      setError(null);
      setSaved(false);
      axios
        .put<CustomerConfig>(`/api/customers/${selectedCustomerId}`, values)
        .then((res) => {
          setConfig(res.data);
          setSaved(true);
          setDirty(false);
        })
        .catch((err) => setError(saveErrorMessage(err)))
        .finally(() => setSaving(false));
    },
    [selectedCustomerId],
  );

  return (
    <Layout user={user}>
      <PageHeading title="Settings" />
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.banner}
        />
      )}
      {listFailed && (
        <Alert
          type="error"
          showIcon
          message="Could not load the customer list."
          action={
            <Button size="small" onClick={loadCustomers}>
              Try again
            </Button>
          }
          className={styles.banner}
        />
      )}
      {!configLoading && configFailed && (
        <Alert
          type="error"
          showIcon
          message="Could not load this customer’s configuration."
          action={
            <Button
              size="small"
              onClick={() => setConfigAttempt((attempt) => attempt + 1)}
            >
              Try again
            </Button>
          }
          className={styles.banner}
        />
      )}
      {isSuperAdmin && (
        <Space direction="vertical" size={4} className={styles.picker}>
          <Text strong>Customer</Text>
          <Space>
            <Select
              placeholder="Choose a customer to configure"
              loading={customersLoading}
              value={selectedCustomerId}
              onChange={handlePickCustomer}
              options={customers.map((c) => ({
                label: customerLabel(c),
                value: c.id,
              }))}
              style={{ minWidth: 360 }}
            />
            <Button icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              New customer
            </Button>
          </Space>
        </Space>
      )}
      {isSuperAdmin && (
        <CreateCustomerModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {configLoading && (
        <div className={styles.loading}>
          <Spin />
        </div>
      )}
      {!configLoading && config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={() => {
            setDirty(true);
            setSaved(false);
          }}
          className={styles.form}
        >
          <section aria-labelledby="settings-fit-scoring">
            <Title
              level={4}
              id="settings-fit-scoring"
              className={styles.heading}
            >
              Fit scoring
            </Title>
            <Text type="secondary" className={styles.sectionIntro}>
              A fit score runs from 0 to 100. Avrio invites a job seeker to
              interview only when their score is at or above the threshold.
              Applicants are job seekers who applied to a job themselves;
              candidates are job seekers your team sourced.
            </Text>
            <Text type="secondary" className={styles.sectionIntro}>
              Leave a threshold blank and Avrio uses each job&rsquo;s own
              setting, or the recommended {RECOMMENDED_THRESHOLD} where a job
              has none. Entering 0 counts as blank: it does not remove the bar.
              A number set here replaces every one of {config.name}
              &rsquo;s per-job thresholds entirely.
            </Text>
            <Form.Item
              name="applicantFitScoreThreshhold"
              label="Applicant fit-score threshold"
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                addonAfter="/ 100"
                placeholder={`Not set (${RECOMMENDED_THRESHOLD})`}
                style={{ width: 220 }}
              />
            </Form.Item>
            <Form.Item
              name="candidateFitScoreThreshhold"
              label="Candidate fit-score threshold"
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                addonAfter="/ 100"
                placeholder={`Not set (${RECOMMENDED_THRESHOLD})`}
                style={{ width: 220 }}
              />
            </Form.Item>
          </section>

          <section aria-labelledby="settings-branding">
            <Title level={4} id="settings-branding" className={styles.heading}>
              Branding
            </Title>
            <Form.Item
              name="logoUrl"
              label="Logo URL"
              extra="A link to an image, starting with https://. There is no upload yet."
              rules={[
                {
                  validator: (_, value?: string) =>
                    !value || !value.trim() || isHttpUrl(value.trim())
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Enter a full link starting with https://'),
                        ),
                },
              ]}
            >
              <Input placeholder="https://…" style={{ maxWidth: 480 }} />
            </Form.Item>
            {logoValue && isHttpUrl(logoValue.trim()) && (
              <div className={styles.logoPreviewBlock}>
                {logoBroken ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="That link did not load as an image."
                    description="Check the address, or that it points straight at the image file."
                  />
                ) : (
                  <Image
                    src={logoValue.trim()}
                    alt={`${config.name} logo preview`}
                    width={120}
                    className={styles.logoPreview}
                    preview={false}
                    onError={() => setLogoBroken(true)}
                  />
                )}
              </div>
            )}
          </section>

          {saved && (
            <Alert
              type="success"
              showIcon
              message="Saved"
              className={styles.banner}
            />
          )}
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              disabled={!dirty}
            >
              Save
            </Button>
            {dirty && <Text type="secondary">Unsaved changes</Text>}
          </Space>
        </Form>
      )}
      {!configLoading && config && isSuperAdmin && selectedCustomerId && (
        <SyntheticCustomerSection
          customerId={selectedCustomerId}
          customerName={config.name}
          isSynthetic={config.isSynthetic === true}
          reason={config.syntheticReason}
          onChanged={handleSyntheticChanged}
        />
      )}
      {!configLoading && config && selectedCustomerId && (
        <CustomerTierSection
          customerId={selectedCustomerId}
          customerName={config.name}
          tier={config.tier}
          canEdit={isSuperAdmin}
          onChanged={handleTierChanged}
        />
      )}
      {!configLoading && config && selectedCustomerId && (
        <CustomerCountrySection
          customerId={selectedCustomerId}
          customerName={config.name}
          countryCode={config.countryCode}
          canEdit={isSuperAdmin}
          onChanged={handleCountryChanged}
        />
      )}
      {!configLoading && config && selectedCustomerId && (
        <SponsorshipPolicySection
          customerId={selectedCustomerId}
          customerName={config.name}
        />
      )}
      {(isSuperAdmin || user?.customerId) && (
        <section aria-labelledby="settings-plan-billing">
          <Title
            level={4}
            id="settings-plan-billing"
            className={styles.heading}
          >
            Plan &amp; billing
          </Title>
          {configLoading && !isSuperAdmin && (
            <Text type="secondary">Loading…</Text>
          )}
          {!configLoading && config && isSuperAdmin && selectedCustomerId && (
            <CustomerPlanSection
              customerId={selectedCustomerId}
              customerName={config.name}
            />
          )}
          {/* Matching limits apply to every customer, self-serve or
              enterprise (SMB self-serve session's sections). */}
          {!configLoading && config && isSuperAdmin && selectedCustomerId && (
            <MatchingUsageSection
              customerId={selectedCustomerId}
              isSuperAdmin
            />
          )}
          {!configLoading && !isSuperAdmin && user?.customerId && (
            <YourPlanSection customerId={user.customerId} />
          )}
          {!configLoading && !isSuperAdmin && user?.customerId && (
            <MatchingUsageSection customerId={user.customerId} />
          )}
          {isSuperAdmin && <EstateMatchingSection />}
        </section>
      )}
      {isSuperAdmin &&
        !customersLoading &&
        !listFailed &&
        customers.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="There are no customers yet."
            description="Use New customer above to create the first one."
            className={styles.banner}
          />
        )}
      {!configLoading &&
        !config &&
        isSuperAdmin &&
        !selectedCustomerId &&
        customers.length > 0 && (
          <Text type="secondary">Choose a customer above to configure it.</Text>
        )}
      {/* An Admin with no customer on their account has nothing to configure.
          Say so, rather than showing the heading and nothing else. */}
      {!isSuperAdmin && !user?.customerId && (
        <Alert
          type="info"
          showIcon
          message="Your account isn’t linked to a customer."
          description="There is nothing to configure here until it is. Contact Avrio."
          className={styles.banner}
        />
      )}
    </Layout>
  );
};

export default memo(CustomerSettings);
